
import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { 
  USER_PROFILE_IMAGE, 
  INITIAL_COMPANY,
  INITIAL_ROLES
} from './constants';
import { Client, Project, Task, Transaction, UserProfile, Role, CompanyInfo, Supplier, Expense, FinancialCategory, SubscriptionPlan, Subscription, SubscriptionInvoice } from './types';
import { 
  mapClient, 
  mapProject, 
  mapTask, 
  mapTransaction, 
  mapSupplier, 
  mapExpense, 
  mapFinancialCategory,
  mapSubscriptionPlan,
  mapSubscription,
  mapSubscriptionInvoice
} from './mappers';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import Projects from './components/Projects';
import Kanban from './components/Kanban';
import Financials from './components/Financials';
import Team from './components/Team';
import Settings from './components/Settings';
import Onboarding from './components/Onboarding';
import Briefings from './components/Briefings';
import Profile from './components/Profile';
import ErpLogin from './components/ui/erp-login';

interface AppContextType {
  clients: Client[];
  projects: Project[];
  tasks: Task[];
  transactions: Transaction[];
  suppliers: Supplier[];
  expenses: Expense[];
  financialCategories: FinancialCategory[];
  subscriptionPlans: SubscriptionPlan[];
  subscriptions: Subscription[];
  subscriptionInvoices: SubscriptionInvoice[];
  profiles: UserProfile[];
  roles: Role[];
  company: CompanyInfo;
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setTransactions: React.Dispatch<React.SetStateAction<Transaction[]>>;
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  setExpenses: React.Dispatch<React.SetStateAction<Expense[]>>;
  setFinancialCategories: React.Dispatch<React.SetStateAction<FinancialCategory[]>>;
  setSubscriptionPlans: React.Dispatch<React.SetStateAction<SubscriptionPlan[]>>;
  setSubscriptions: React.Dispatch<React.SetStateAction<Subscription[]>>;
  setSubscriptionInvoices: React.Dispatch<React.SetStateAction<SubscriptionInvoice[]>>;
  setProfiles: React.Dispatch<React.SetStateAction<UserProfile[]>>;
  setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  setCompany: React.Dispatch<React.SetStateAction<CompanyInfo>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  currentUser: UserProfile | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  syncAllData: () => Promise<void>;
  handleLogout: () => Promise<void>;
}

export const AppContext = createContext<AppContextType | null>(null);

const FirstLoginOverlay = () => {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  if (!context?.currentUser?.isFirstLogin) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-text-main/95 backdrop-blur-xl flex items-center justify-center p-6 text-center font-display">
      <div className="max-w-md w-full flex flex-col items-center gap-8 animate-in zoom-in duration-500">
        <div className="size-24 rounded-[32px] bg-primary flex items-center justify-center text-white shadow-2xl shadow-primary/20">
          <span className="material-symbols-outlined text-5xl">lock_reset</span>
        </div>
        <div className="space-y-3">
          <h2 className="text-3xl font-black text-white tracking-tight">Primeiro Acesso</h2>
          <p className="text-white/60 text-sm font-bold leading-relaxed">Defina uma nova senha para sua conta por segurança.</p>
        </div>
        <button 
          onClick={() => navigate('/profile')}
          className="h-16 w-full bg-white text-primary rounded-2xl font-black uppercase tracking-widest shadow-xl hover:scale-[1.02] active:scale-95 transition-all"
        >
          Redefinir Senha
        </button>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const context = useContext(AppContext);
  const isActive = (path: string) => location.pathname === path;

  if (!context) return null;

  const navItems = [
    { path: '/', label: 'Painel', icon: 'dashboard' },
    { path: '/clients', label: 'Clientes', icon: 'group' },
    { path: '/briefings', label: 'Briefings', icon: 'description' },
    { path: '/projects', label: 'Projetos', icon: 'folder' },
    { path: '/kanban', label: 'Kanban', icon: 'view_kanban' },
    { path: '/financials', label: 'Financeiro', icon: 'payments' },
    { path: '/subscriptions', label: 'Assinaturas', icon: 'subscriptions' },
    { path: '/invoices', label: 'Faturas', icon: 'receipt_long' },
    { path: '/team', label: 'Equipe', icon: 'badge' },
  ];

  return (
    <>
      {context.sidebarOpen && (
        <div className="fixed inset-0 bg-text-main/40 backdrop-blur-sm z-40 lg:hidden" onClick={() => context.setSidebarOpen(false)} />
      )}
      <aside className={`fixed inset-y-0 left-0 w-72 flex-shrink-0 flex flex-col border-r border-gray-100 bg-white h-full z-50 transform transition-transform duration-300 lg:relative lg:translate-x-0 ${context.sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <div className="bg-primary flex items-center justify-center rounded-xl size-10 text-white shadow-lg shadow-primary/30 overflow-hidden shrink-0">
                {context.company.logoUrl ? (
                  <img src={context.company.logoUrl} className="w-full h-full object-contain" alt="Logo" />
                ) : (
                  <span className="material-symbols-outlined text-2xl">hexagon</span>
                )}
              </div>
              <div className="flex flex-col">
                <h1 className="text-primary text-base font-extrabold tracking-tight truncate max-w-[140px] uppercase leading-none">
                  {context.company.name.split(' ')[0] || 'Magenta'}
                </h1>
                <p className="text-text-secondary text-[8px] font-black uppercase tracking-widest mt-1">SISTEMA ERP</p>
              </div>
            </div>
            <button onClick={() => context.setSidebarOpen(false)} className="lg:hidden size-10 flex items-center justify-center rounded-xl hover:bg-gray-100">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <nav className="flex flex-col gap-1.5 overflow-y-auto scrollbar-hide">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => context.setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary font-bold shadow-sm'
                    : 'text-text-secondary hover:bg-gray-50 hover:text-text-main font-medium'
                }`}
              >
                <span className={`material-symbols-outlined ${isActive(item.path) ? 'filled' : ''}`}>
                  {item.icon}
                </span>
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
            <Link
              to="/settings"
              onClick={() => context.setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive('/settings')
                  ? 'bg-primary/10 text-primary font-bold shadow-sm'
                  : 'text-text-secondary hover:bg-gray-50 hover:text-text-main font-medium'
              }`}
            >
              <span className={`material-symbols-outlined ${isActive('/settings') ? 'filled' : ''}`}>settings</span>
              <span className="text-sm">Configurações</span>
            </Link>
          </nav>

          <div className="mt-auto pt-6 border-t border-gray-50">
            <Link to="/profile" className="flex items-center gap-3 hover:bg-gray-50 p-2 rounded-2xl transition-all group">
              <div 
                className="size-10 rounded-full bg-center bg-cover border-2 border-white ring-2 ring-primary/10 shrink-0 group-hover:ring-primary/30 transition-all bg-gray-100 flex items-center justify-center overflow-hidden shadow-sm" 
                style={{ backgroundImage: context.currentUser?.avatarUrl ? `url(${context.currentUser.avatarUrl})` : 'none' }}
              >
                {!context.currentUser?.avatarUrl && <span className="material-symbols-outlined text-gray-400">person</span>}
              </div>
              <div className="flex flex-col overflow-hidden">
                <p className="text-text-main text-xs font-black truncate group-hover:text-primary">{context.currentUser?.name}</p>
                <p className="text-text-secondary text-[9px] truncate uppercase tracking-widest font-bold">{context.currentUser?.role}</p>
              </div>
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = () => {
  const context = useContext(AppContext);
  if (!context) return null;
  const { searchTerm, setSearchTerm } = context;

  return (
    <header className="h-20 bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 md:px-8 flex items-center justify-between shrink-0 z-20 sticky top-0 font-display">
      <div className="flex items-center gap-3 md:gap-4 flex-1">
        <button 
          onClick={() => context.setSidebarOpen(true)}
          className="lg:hidden size-10 flex items-center justify-center rounded-xl text-text-secondary hover:bg-gray-100 shrink-0 transition-all"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>
        <div className="relative w-full max-w-sm group">
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-[18px]">search</span>
          <input 
            className="w-full h-11 pl-11 pr-4 bg-gray-50/50 border border-transparent rounded-2xl text-xs font-bold focus:bg-white focus:border-primary/10 transition-all placeholder:text-gray-400" 
            placeholder="Pesquisar no sistema..." 
            type="text" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Link 
          to="/profile"
          className="size-10 rounded-full bg-center bg-cover border-2 border-white ring-1 ring-primary/10 transition-transform hover:scale-110 active:scale-95 bg-gray-100 flex items-center justify-center overflow-hidden shadow-sm" 
          style={{ backgroundImage: context.currentUser?.avatarUrl ? `url(${context.currentUser.avatarUrl})` : 'none' }}
        >
          {!context.currentUser?.avatarUrl && <span className="material-symbols-outlined text-gray-400 text-base">person</span>}
        </Link>
      </div>
    </header>
  );
};

const formatDateOnly = (date: Date) => date.toISOString().slice(0, 10);

const parseDateOnly = (value?: string | null) => {
  if (!value) return null;
  return new Date(`${value}T00:00:00`);
};

const getPeriodRef = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const getNextPeriodRef = (periodRef: string) => {
  const [yearText, monthText] = periodRef.split('-');
  const year = parseInt(yearText);
  const month = parseInt(monthText);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
};

const getDueDateForPeriod = (periodRef: string, dueDay: number) => {
  const [yearText, monthText] = periodRef.split('-');
  const year = parseInt(yearText);
  const monthIndex = parseInt(monthText) - 1;
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const day = Math.min(Math.max(dueDay || 1, 1), lastDay);
  return new Date(year, monthIndex, day);
};

const getDaysDiff = (future: Date, past: Date) => Math.ceil((future.getTime() - past.getTime()) / (1000 * 60 * 60 * 24));

const formatCurrency = (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const formatDateBr = (value?: string | null) => {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const formatTransactionDate = (date: Date) => date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const getInvoiceTransactionId = (invoiceId: string) => `TRX-${invoiceId}`;

type SubTab = 'dashboard' | 'customers' | 'plans' | 'reports';

const Subscriptions = () => {
  const context = useContext(AppContext);
  const [activeTab, setActiveTab] = useState<SubTab>('dashboard');
  const [planName, setPlanName] = useState('');
  const [planPrice, setPlanPrice] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'paid' | 'overdue' | 'dueSoon'>('all');
  const [filterPlan, setFilterPlan] = useState<'all' | 'personal' | string>('all');
  const [filterMinValue, setFilterMinValue] = useState('');
  const [filterMaxValue, setFilterMaxValue] = useState('');
  const [filterDueFrom, setFilterDueFrom] = useState('');
  const [filterDueTo, setFilterDueTo] = useState('');

  if (!context) return null;
  const { clients, subscriptionPlans, subscriptions, subscriptionInvoices, syncAllData, setSubscriptions, setSubscriptionInvoices } = context;

  const currentPeriod = getPeriodRef(new Date());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [reportYear, setReportYear] = useState(currentPeriod.split('-')[0]);
  const [reportMonth, setReportMonth] = useState(currentPeriod.split('-')[1]);

  const getSubscription = (clientId: string) => {
    const existing = subscriptions.find(sub => sub.clientId === clientId);
    if (existing) return { ...existing, planId: existing.planId || '' };
    return { id: '', clientId, enabled: false, monthlyValue: 0, dueDay: 5, planId: '', startDate: null, cancelDate: null };
  };

  const saveSubscription = async (clientId: string, patch: Partial<Subscription>) => {
    const existing = subscriptions.find(sub => sub.clientId === clientId);
    const todayText = formatDateOnly(new Date());
    const base: Subscription = existing || {
      id: `sub-${clientId}`,
      clientId,
      enabled: false,
      monthlyValue: 0,
      dueDay: 5,
      planId: null,
      startDate: null,
      cancelDate: null
    };

    let next: Subscription = {
      ...base,
      ...patch,
      planId: patch.planId === '' ? null : patch.planId ?? base.planId
    };

    if (patch.enabled === true && !base.enabled) next = { ...next, startDate: base.startDate || todayText, cancelDate: null };
    if (patch.enabled === false && base.enabled) next = { ...next, cancelDate: todayText };

    const previousSubscriptions = subscriptions;
    setSubscriptions(prev => {
      const existingIndex = prev.findIndex(item => item.clientId === clientId);
      if (existingIndex === -1) return [next, ...prev];
      return prev.map(item => item.clientId === clientId ? next : item);
    });

    const { error } = await supabase.from('subscriptions').upsert({
      id: next.id,
      client_id: next.clientId,
      enabled: next.enabled,
      monthly_value: next.monthlyValue,
      due_day: next.dueDay,
      plan_id: next.planId,
      start_date: next.startDate,
      cancel_date: next.cancelDate
    }, { onConflict: 'client_id' });
    if (error) {
      setSubscriptions(previousSubscriptions);
      console.error('Erro ao salvar assinatura:', error);
      alert(`Erro ao salvar assinatura: ${error.message}`);
    }
    if (!error) await syncAllData();
  };

  const setPaidForClient = async (clientId: string, paid: boolean) => {
    const sub = getSubscription(clientId);
    if (!sub.enabled) return;

    const dueDate = getDueDateForPeriod(currentPeriod, sub.dueDay || 5);
    const issueDate = formatDateOnly(new Date());
    const existingInvoice = subscriptionInvoices.find(i => i.clientId === clientId && i.periodRef === currentPeriod);
    const subscriptionId = sub.id || `sub-${clientId}`;

    if (!sub.id) {
      const { error } = await supabase.from('subscriptions').upsert({
        id: subscriptionId,
        client_id: clientId,
        enabled: true,
        monthly_value: sub.monthlyValue,
        due_day: sub.dueDay,
        plan_id: sub.planId || null,
        start_date: sub.startDate || issueDate,
        cancel_date: sub.cancelDate
      }, { onConflict: 'client_id' });
      if (error) {
        console.error('Erro ao criar assinatura:', error);
        alert('Erro ao criar assinatura. Verifique o console.');
      }
    }

    const invoicePayload = {
      id: existingInvoice?.id || `inv-${subscriptionId}-${currentPeriod}`,
      subscription_id: subscriptionId,
      client_id: clientId,
      period_ref: currentPeriod,
      issue_date: existingInvoice?.issueDate || issueDate,
      due_date: existingInvoice?.dueDate || formatDateOnly(dueDate),
      amount: sub.monthlyValue || 0,
      status: paid ? 'Paid' : 'Open',
      paid_at: paid ? new Date().toISOString() : null,
      paid_amount: paid ? sub.monthlyValue || 0 : null
    };

    const previousInvoices = subscriptionInvoices;
    setSubscriptionInvoices(prev => {
      const exists = prev.find(i => i.id === invoicePayload.id);
      const nextInvoice: SubscriptionInvoice = {
        id: invoicePayload.id,
        subscriptionId: invoicePayload.subscription_id,
        clientId: invoicePayload.client_id,
        periodRef: invoicePayload.period_ref,
        issueDate: invoicePayload.issue_date,
        dueDate: invoicePayload.due_date,
        amount: invoicePayload.amount,
        status: paid ? 'Paid' : 'Open',
        paidAt: invoicePayload.paid_at,
        paidAmount: invoicePayload.paid_amount
      };
      if (!exists) return [nextInvoice, ...prev];
      return prev.map(item => item.id === invoicePayload.id ? nextInvoice : item);
    });

    const { error: invoiceError } = await supabase.from('subscription_invoices').upsert(invoicePayload, { onConflict: 'subscription_id,period_ref' });
    const clientName = clients.find(c => c.id === clientId)?.name || 'Cliente';
    const planName = sub.planId ? (subscriptionPlans.find(p => p.id === sub.planId)?.name || 'Plano removido') : 'Personalizado';
    const transactionId = getInvoiceTransactionId(invoicePayload.id);
    if (paid) {
      const { error: trxError } = await supabase.from('transactions').upsert({
        id: transactionId,
        client: clientName,
        plan: planName,
        date: formatTransactionDate(new Date()),
        amount: invoicePayload.amount || 0,
        status: 'Paid',
        initials: clientName.substring(0, 2).toUpperCase()
      }, { onConflict: 'id' });
      if (trxError) {
        setSubscriptionInvoices(previousInvoices);
        console.error('Erro ao lançar receita:', trxError);
        alert(`Erro ao lançar receita: ${trxError.message}`);
      }
    } else {
      const { error: trxDeleteError } = await supabase.from('transactions').delete().eq('id', transactionId);
      if (trxDeleteError) {
        setSubscriptionInvoices(previousInvoices);
        console.error('Erro ao remover receita:', trxDeleteError);
        alert(`Erro ao remover receita: ${trxDeleteError.message}`);
      }
    }
    if (invoiceError) {
      setSubscriptionInvoices(previousInvoices);
      console.error('Erro ao salvar fatura:', invoiceError);
      alert(`Erro ao salvar fatura: ${invoiceError.message}`);
    }
    if (!invoiceError) await syncAllData();
  };

  const enabledSubs = clients.filter(c => getSubscription(c.id).enabled);
  const paidSubs = enabledSubs.filter(c => {
    const invoice = subscriptionInvoices.find(i => i.clientId === c.id && i.periodRef === currentPeriod);
    return Boolean(invoice?.paidAt);
  });
  const overdueSubs = enabledSubs.filter(c => {
    const sub = getSubscription(c.id);
    const dueDate = getDueDateForPeriod(currentPeriod, sub.dueDay || 5);
    const invoice = subscriptionInvoices.find(i => i.clientId === c.id && i.periodRef === currentPeriod);
    return !invoice?.paidAt && dueDate < today;
  });
  const dueSoonSubs = enabledSubs.filter(c => {
    const sub = getSubscription(c.id);
    const dueDate = getDueDateForPeriod(currentPeriod, sub.dueDay || 5);
    const invoice = subscriptionInvoices.find(i => i.clientId === c.id && i.periodRef === currentPeriod);
    return !invoice?.paidAt && dueDate >= today && getDaysDiff(dueDate, today) <= 5;
  });
  const totalMRR = enabledSubs.reduce((acc, c) => acc + (getSubscription(c.id).monthlyValue || 0), 0);
  const periodOptions = Array.from(new Set(subscriptionInvoices.map(inv => inv.periodRef).concat(currentPeriod))).sort().reverse();
  const reportYears = Array.from(new Set(periodOptions.map(period => period.split('-')[0]).concat(currentPeriod.split('-')[0]))).sort().reverse();
  const reportPeriod = `${reportYear}-${reportMonth}`;
  const reportMonths = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Fev' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Abr' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Ago' },
    { value: '09', label: 'Set' },
    { value: '10', label: 'Out' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dez' }
  ];
  const reportInvoices = subscriptionInvoices.filter(inv => inv.periodRef === reportPeriod);
  const reportPaid = reportInvoices.filter(inv => inv.paidAt);
  const reportOverdue = reportInvoices.filter(inv => !inv.paidAt && (parseDateOnly(inv.dueDate) || today) < today);
  const reportOpen = reportInvoices.filter(inv => !inv.paidAt && (parseDateOnly(inv.dueDate) || today) >= today);
  const reportPaidAmount = reportPaid.reduce((sum, inv) => sum + (inv.paidAmount || inv.amount || 0), 0);
  const reportOverdueAmount = reportOverdue.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const reportOpenAmount = reportOpen.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const reportTotalExpected = reportPaidAmount + reportOpenAmount + reportOverdueAmount;
  const reportCollectionRate = reportTotalExpected > 0 ? (reportPaidAmount / reportTotalExpected) * 100 : 0;
  const reportDelinquencyRate = reportTotalExpected > 0 ? (reportOverdueAmount / reportTotalExpected) * 100 : 0;
  const enabledSubscriptions = subscriptions.filter(sub => sub.enabled);
  const avgTicket = enabledSubscriptions.length ? totalMRR / enabledSubscriptions.length : 0;
  const isInReportPeriod = (value?: string | null) => value ? getPeriodRef(new Date(`${value}T00:00:00`)) === reportPeriod : false;
  const newSubsInPeriod = subscriptions.filter(sub => isInReportPeriod(sub.startDate)).length;
  const canceledInPeriod = subscriptions.filter(sub => isInReportPeriod(sub.cancelDate)).length;
  const overdueRanking = reportOverdue
    .map(inv => ({
      ...inv,
      daysLate: Math.max(0, getDaysDiff(today, parseDateOnly(inv.dueDate) || today))
    }))
    .sort((a, b) => b.daysLate - a.daysLate)
    .slice(0, 5);
  const formatCsvValue = (value: string | number | null | undefined) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const getInvoiceStatusLabel = (invoice: SubscriptionInvoice) => {
    if (invoice.paidAt) return 'Pago';
    const dueDate = parseDateOnly(invoice.dueDate) || today;
    return dueDate < today ? 'Em atraso' : 'Em aberto';
  };
  const exportReportCsv = () => {
    const summaryRows = [
      ['Período', reportPeriod],
      ['Assinaturas ativas', enabledSubs.length],
      ['Ticket médio', formatCurrency(avgTicket)],
      ['Receita recebida', formatCurrency(reportPaidAmount)],
      ['Receita em aberto', formatCurrency(reportOpenAmount)],
      ['Receita em atraso', formatCurrency(reportOverdueAmount)],
      ['Previsão total', formatCurrency(reportTotalExpected)],
      ['Taxa de recebimento', `${reportCollectionRate.toFixed(1)}%`],
      ['Inadimplência', `${reportDelinquencyRate.toFixed(1)}%`],
      ['Novas no período', newSubsInPeriod],
      ['Canceladas no período', canceledInPeriod]
    ];
    const invoiceRows = reportInvoices
      .slice()
      .sort((a, b) => (parseDateOnly(a.dueDate)?.getTime() || 0) - (parseDateOnly(b.dueDate)?.getTime() || 0))
      .map(inv => {
        const clientName = clients.find(c => c.id === inv.clientId)?.name || 'Cliente';
        const subscription = subscriptions.find(s => s.id === inv.subscriptionId);
        const planName = subscription?.planId ? (subscriptionPlans.find(p => p.id === subscription.planId)?.name || 'Plano removido') : 'Personalizado';
        const dueDate = parseDateOnly(inv.dueDate) || today;
        const daysLate = inv.paidAt ? 0 : Math.max(0, getDaysDiff(today, dueDate));
        return [
          clientName,
          planName,
          inv.periodRef,
          getInvoiceStatusLabel(inv),
          formatDateBr(inv.dueDate),
          inv.paidAt ? new Date(inv.paidAt).toLocaleDateString('pt-BR') : '',
          inv.amount || 0,
          inv.paidAmount || '',
          daysLate
        ];
      });
    const header = ['Cliente', 'Plano', 'Período', 'Status', 'Vencimento', 'Pago em', 'Valor', 'Valor pago', 'Dias em atraso'];
    const lines = [
      ...summaryRows.map(row => row.map(formatCsvValue).join(';')),
      '',
      header.map(formatCsvValue).join(';'),
      ...invoiceRows.map(row => row.map(formatCsvValue).join(';'))
    ];
    const csv = `\uFEFF${lines.join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-assinaturas-${reportPeriod}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const normalizedSearch = filterSearch.trim().toLowerCase();
  const minValue = filterMinValue ? parseFloat(filterMinValue) : null;
  const maxValue = filterMaxValue ? parseFloat(filterMaxValue) : null;
  const dueFrom = filterDueFrom ? parseInt(filterDueFrom) : null;
  const dueTo = filterDueTo ? parseInt(filterDueTo) : null;

  const filteredClients = clients.filter(client => {
    const sub = getSubscription(client.id);
    const dueDate = getDueDateForPeriod(currentPeriod, sub.dueDay || 5);
    const invoice = subscriptionInvoices.find(i => i.clientId === client.id && i.periodRef === currentPeriod);
    const paidThisMonth = Boolean(invoice?.paidAt);
    const overdue = sub.enabled && !paidThisMonth && dueDate < today;
    const dueSoon = sub.enabled && !paidThisMonth && dueDate >= today && getDaysDiff(dueDate, today) <= 5;
    const status = !sub.enabled ? 'inactive' : paidThisMonth ? 'paid' : overdue ? 'overdue' : dueSoon ? 'dueSoon' : 'active';
    if (normalizedSearch && !client.name.toLowerCase().includes(normalizedSearch)) return false;
    if (filterStatus !== 'all' && status !== filterStatus) return false;
    if (filterPlan === 'personal' && sub.planId) return false;
    if (filterPlan !== 'all' && filterPlan !== 'personal' && (sub.planId || '') !== filterPlan) return false;
    if (minValue !== null && (sub.monthlyValue || 0) < minValue) return false;
    if (maxValue !== null && (sub.monthlyValue || 0) > maxValue) return false;
    if (dueFrom !== null && sub.dueDay < dueFrom) return false;
    if (dueTo !== null && sub.dueDay > dueTo) return false;
    return true;
  });

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-7xl mx-auto flex flex-col gap-6 md:gap-10">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Assinaturas</h2>
          <p className="text-text-secondary font-bold text-base md:text-lg">Gestão de recorrência por cliente.</p>
        </div>
      </header>

      <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide pb-2 md:pb-0">
        <div className="flex gap-2 p-1.5 bg-gray-100/60 rounded-[23px] w-fit min-w-full md:min-w-0">
          {[
            { id: 'dashboard', label: 'Painel' },
            { id: 'customers', label: 'Assinaturas' },
            { id: 'plans', label: 'Planos' },
            { id: 'reports', label: 'Relatórios' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as SubTab)} className={`px-6 md:px-8 py-3.5 rounded-[22px] text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-1 md:flex-none ${activeTab === tab.id ? 'bg-white shadow-md text-primary' : 'text-text-secondary hover:text-text-main'}`}>{tab.label}</button>
          ))}
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
            <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Ativas</p>
            <p className="text-3xl md:text-4xl font-black text-text-main mt-3 tracking-tighter">{enabledSubs.length}</p>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
            <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Em Atraso</p>
            <p className="text-3xl md:text-4xl font-black text-red-500 mt-3 tracking-tighter">{overdueSubs.length}</p>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
            <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">A Vencer</p>
            <p className="text-3xl md:text-4xl font-black text-orange-500 mt-3 tracking-tighter">{dueSoonSubs.length}</p>
          </div>
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
            <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">MRR</p>
            <p className="text-3xl md:text-4xl font-black text-primary mt-3 tracking-tighter">R$ {totalMRR.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
            <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-3">Pagas: {paidSubs.length}</p>
          </div>
        </div>
      )}

      {activeTab === 'customers' && (
        <div className="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-500">
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Buscar cliente</label>
                <input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" placeholder="Nome do cliente" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Status</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
                  <option value="all">Todos</option>
                  <option value="active">Ativas</option>
                  <option value="inactive">Inativas</option>
                  <option value="paid">Pagas</option>
                  <option value="overdue">Em atraso</option>
                  <option value="dueSoon">A vencer</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Plano</label>
                <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
                  <option value="all">Todos</option>
                  <option value="personal">Personalizado</option>
                  {subscriptionPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary">Valor mín.</label>
                  <input value={filterMinValue} onChange={(e) => setFilterMinValue(e.target.value)} type="number" min="0" step="0.01" className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary">Valor máx.</label>
                  <input value={filterMaxValue} onChange={(e) => setFilterMaxValue(e.target.value)} type="number" min="0" step="0.01" className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary">Venc. de</label>
                  <input value={filterDueFrom} onChange={(e) => setFilterDueFrom(e.target.value)} type="number" min="1" max="31" className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-black uppercase text-text-secondary">Venc. até</label>
                  <input value={filterDueTo} onChange={(e) => setFilterDueTo(e.target.value)} type="number" min="1" max="31" className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setFilterSearch('');
                    setFilterStatus('all');
                    setFilterPlan('all');
                    setFilterMinValue('');
                    setFilterMaxValue('');
                    setFilterDueFrom('');
                    setFilterDueTo('');
                  }}
                  className="h-12 px-6 rounded-2xl bg-gray-100 text-text-secondary font-black uppercase tracking-widest text-[10px]"
                >
                  Limpar filtros
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[23px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-100 text-[9px] font-black uppercase tracking-widest text-text-secondary">
              <span className="col-span-3">Cliente</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-3">Plano</span>
              <span className="col-span-2">Valor mensal</span>
              <span className="col-span-2">Vencimento</span>
            </div>
            <div className="divide-y divide-gray-100">
              {filteredClients.map(client => {
                const sub = getSubscription(client.id);
                const currentDueDate = getDueDateForPeriod(currentPeriod, sub.dueDay || 5);
                const startDate = parseDateOnly(sub.startDate);
                const targetPeriod = startDate && startDate > currentDueDate ? getNextPeriodRef(currentPeriod) : currentPeriod;
                const dueDate = getDueDateForPeriod(targetPeriod, sub.dueDay || 5);
                const invoice = subscriptionInvoices.find(i => i.clientId === client.id && i.periodRef === targetPeriod);
                const paidThisMonth = Boolean(invoice?.paidAt);
                const overdue = sub.enabled && !paidThisMonth && dueDate < today;
                const dueSoon = sub.enabled && !paidThisMonth && dueDate >= today && getDaysDiff(dueDate, today) <= 5;
                const statusLabel = sub.enabled ? (paidThisMonth ? 'Pago' : overdue ? 'Em Atraso' : dueSoon ? 'A Vencer' : 'Ativo') : 'Inativo';
                const statusClass = sub.enabled ? (paidThisMonth ? 'bg-green-50 text-green-600' : overdue ? 'bg-red-50 text-red-600' : dueSoon ? 'bg-orange-50 text-orange-600' : 'bg-primary/10 text-primary') : 'bg-gray-50 text-text-secondary';
                const hasPlan = sub.planId ? subscriptionPlans.some(p => p.id === sub.planId) : true;
                const triggerDate = new Date(dueDate);
                triggerDate.setDate(dueDate.getDate() - 15);
                const generationPending = sub.enabled && !invoice && today < triggerDate;
                const daysUntilGeneration = generationPending ? getDaysDiff(triggerDate, today) : 0;

                return (
                  <div key={client.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 md:items-center">
                    <div className="md:col-span-3 min-w-0">
                      <p className="text-sm font-black text-text-main truncate">{client.name}</p>
                      <p className="text-[9px] font-black uppercase tracking-widest text-text-secondary mt-1">Cliente</p>
                    </div>
                    <div className="md:col-span-2 flex flex-col gap-2">
                      <span className={`w-fit px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${statusClass}`}>{statusLabel}</span>
                      {generationPending && (
                        <span className="w-fit px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-gray-50 text-text-secondary">
                          Gera em {daysUntilGeneration}d
                        </span>
                      )}
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-text-secondary">
                          <input
                            type="checkbox"
                            checked={sub.enabled}
                            onChange={(e) => { void saveSubscription(client.id, { enabled: e.target.checked }); }}
                            className="size-4 accent-primary"
                          />
                          Ativa
                        </label>
                        <label className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-text-secondary">
                          <input
                            type="checkbox"
                            checked={paidThisMonth}
                            onChange={(e) => { void setPaidForClient(client.id, e.target.checked); }}
                            className="size-4 accent-primary"
                          />
                          Pago
                        </label>
                      </div>
                    </div>
                    <div className="md:col-span-3 flex flex-col gap-2">
                      <select
                        value={sub.planId}
                        onChange={(e) => {
                          const planId = e.target.value;
                          const plan = subscriptionPlans.find(p => p.id === planId);
                          void saveSubscription(client.id, { planId, monthlyValue: plan ? plan.price : sub.monthlyValue });
                        }}
                        className="h-11 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all"
                      >
                        <option value="">Personalizado</option>
                        {!hasPlan && sub.planId && <option value={sub.planId}>Plano removido</option>}
                        {subscriptionPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={sub.monthlyValue}
                        onChange={(e) => { void saveSubscription(client.id, { monthlyValue: parseFloat(e.target.value || '0') }); }}
                        className="h-11 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all w-full"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={sub.dueDay}
                        onChange={(e) => { void saveSubscription(client.id, { dueDay: Math.min(31, Math.max(1, parseInt(e.target.value || '1'))) }); }}
                        className="h-11 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all w-full"
                      />
                    </div>
                  </div>
                );
              })}
              {filteredClients.length === 0 && (
                <div className="p-10 text-center text-text-secondary text-sm font-bold">Nenhum cliente encontrado com esses filtros.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'plans' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 animate-in slide-in-from-right-4 duration-500">
          <div className="lg:col-span-1 bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-4">
            <h3 className="text-lg font-black text-text-main">Novo Plano</h3>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase text-text-secondary">Nome do plano</label>
              <input value={planName} onChange={(e) => setPlanName(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase text-text-secondary">Valor mensal</label>
              <input value={planPrice} onChange={(e) => setPlanPrice(e.target.value)} type="number" min="0" step="0.01" className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
            </div>
            <button
              type="button"
              onClick={() => {
                if (!planName.trim()) return;
                const newPlan = { id: `plan-${Date.now()}`, name: planName.trim(), price: parseFloat(planPrice || '0') };
                void supabase.from('subscription_plans').insert({
                  id: newPlan.id,
                  name: newPlan.name,
                  price: newPlan.price
                }).then(async () => {
                  setPlanName('');
                  setPlanPrice('');
                  await syncAllData();
                });
              }}
              className="h-12 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-lg shadow-primary/20"
            >
              Adicionar
            </button>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {subscriptionPlans.length === 0 && (
              <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex items-center justify-center text-text-secondary text-sm font-bold">
                Nenhum plano cadastrado.
              </div>
            )}
            {subscriptionPlans.map(p => (
              <div key={p.id} className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-3">
                <h4 className="text-lg font-black text-text-main">{p.name}</h4>
                <p className="text-2xl font-black text-primary">{formatCurrency(p.price)}</p>
                <p className="text-[9px] font-black uppercase tracking-widest text-text-secondary">Mensal</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-500">
          <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Período</label>
                <div className="grid grid-cols-2 gap-3">
                  <select value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
                    {reportMonths.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                  <select value={reportYear} onChange={(e) => setReportYear(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
                    {reportYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Assinaturas ativas</label>
                <div className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 flex items-center font-black text-sm text-text-main">{enabledSubs.length}</div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Ticket médio</label>
                <div className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 flex items-center font-black text-sm text-text-main">{formatCurrency(avgTicket)}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={exportReportCsv}
                className="h-12 px-6 rounded-2xl bg-primary text-white font-black uppercase tracking-widest text-[10px] shadow-lg shadow-primary/20"
              >
                Exportar CSV
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
            <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
              <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Receita recebida</p>
              <p className="text-3xl md:text-4xl font-black text-green-600 mt-3 tracking-tighter">{formatCurrency(reportPaidAmount)}</p>
              <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-3">Pagas: {reportPaid.length}</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
              <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Receita em aberto</p>
              <p className="text-3xl md:text-4xl font-black text-primary mt-3 tracking-tighter">{formatCurrency(reportOpenAmount)}</p>
              <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-3">Em aberto: {reportOpen.length}</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
              <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Receita em atraso</p>
              <p className="text-3xl md:text-4xl font-black text-red-500 mt-3 tracking-tighter">{formatCurrency(reportOverdueAmount)}</p>
              <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-3">Em atraso: {reportOverdue.length}</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
              <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-text-secondary">Previsão total</p>
              <p className="text-3xl md:text-4xl font-black text-text-main mt-3 tracking-tighter">{formatCurrency(reportTotalExpected)}</p>
              <p className="text-[9px] font-black text-text-secondary uppercase tracking-widest mt-3">Taxa recebimento: {reportCollectionRate.toFixed(1)}%</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-4">
              <h4 className="text-lg font-black text-text-main">Indicadores</h4>
              <div className="flex items-center justify-between text-[10px] md:text-[11px] font-black uppercase tracking-widest text-text-secondary">
                <span>Inadimplência</span>
                <span className="text-red-500">{reportDelinquencyRate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between text-[10px] md:text-[11px] font-black uppercase tracking-widest text-text-secondary">
                <span>Novas no período</span>
                <span className="text-text-main">{newSubsInPeriod}</span>
              </div>
              <div className="flex items-center justify-between text-[10px] md:text-[11px] font-black uppercase tracking-widest text-text-secondary">
                <span>Canceladas no período</span>
                <span className="text-text-main">{canceledInPeriod}</span>
              </div>
              <div className="flex items-center justify-between text-[10px] md:text-[11px] font-black uppercase tracking-widest text-text-secondary">
                <span>Assinaturas inativas</span>
                <span className="text-text-main">{clients.length - enabledSubs.length}</span>
              </div>
            </div>
            <div className="lg:col-span-2 bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm">
              <h4 className="text-lg font-black text-text-main mb-4">Maiores atrasos</h4>
              <div className="space-y-3">
                {overdueRanking.map(inv => {
                  const clientName = clients.find(c => c.id === inv.clientId)?.name || 'Cliente';
                  return (
                    <div key={inv.id} className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-black text-text-main truncate">{clientName}</p>
                        <p className="text-[9px] font-black uppercase tracking-widest text-text-secondary">{inv.periodRef}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-black text-red-500">{formatCurrency(inv.amount || 0)}</p>
                        <p className="text-[9px] font-black uppercase tracking-widest text-text-secondary">{inv.daysLate} dias</p>
                      </div>
                    </div>
                  );
                })}
                {overdueRanking.length === 0 && (
                  <div className="text-text-secondary text-sm font-bold">Nenhum atraso no período.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type InvoiceTab = 'open' | 'overdue' | 'paid';

const Invoices = () => {
  const context = useContext(AppContext);
  const [activeTab, setActiveTab] = useState<InvoiceTab>('open');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');

  if (!context) return null;
  const { clients, subscriptions, subscriptionInvoices, subscriptionPlans, syncAllData, setSubscriptionInvoices } = context;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getStatus = (invoice: SubscriptionInvoice) => {
    if (invoice.paidAt) return 'paid';
    const dueDate = parseDateOnly(invoice.dueDate) || today;
    if (dueDate < today) return 'overdue';
    return 'open';
  };

  const openCount = subscriptionInvoices.filter(inv => getStatus(inv) === 'open').length;
  const overdueCount = subscriptionInvoices.filter(inv => getStatus(inv) === 'overdue').length;
  const paidCount = subscriptionInvoices.filter(inv => getStatus(inv) === 'paid').length;
  const periodOptions = Array.from(new Set(subscriptionInvoices.map(inv => inv.periodRef))).sort().reverse();

  const normalizedSearch = filterSearch.trim().toLowerCase();
  const filteredInvoices = subscriptionInvoices.filter(inv => {
    const status = getStatus(inv);
    if (status !== activeTab) return false;
    if (filterPeriod !== 'all' && inv.periodRef !== filterPeriod) return false;
    if (!normalizedSearch) return true;
    const clientName = clients.find(c => c.id === inv.clientId)?.name || '';
    return clientName.toLowerCase().includes(normalizedSearch);
  });

  const setInvoicePaid = async (invoice: SubscriptionInvoice, paid: boolean) => {
    const paidAt = paid ? new Date() : null;
    const previousInvoices = subscriptionInvoices;
    setSubscriptionInvoices(prev => prev.map(item => item.id === invoice.id ? {
      ...item,
      status: paid ? 'Paid' : 'Open',
      paidAt: paidAt ? paidAt.toISOString() : null,
      paidAmount: paid ? invoice.amount : null
    } : item));
    const { error: invoiceError } = await supabase.from('subscription_invoices').update({
      paid_at: paidAt ? paidAt.toISOString() : null,
      status: paid ? 'Paid' : 'Open',
      paid_amount: paid ? invoice.amount : null
    }).eq('id', invoice.id);
    const clientName = clients.find(c => c.id === invoice.clientId)?.name || 'Cliente';
    const subscription = subscriptions.find(s => s.id === invoice.subscriptionId);
    const planName = subscription?.planId ? (subscriptionPlans.find(p => p.id === subscription.planId)?.name || 'Plano removido') : 'Personalizado';
    const transactionId = getInvoiceTransactionId(invoice.id);
    if (paid) {
      const { error: trxError } = await supabase.from('transactions').upsert({
        id: transactionId,
        client: clientName,
        plan: planName,
        date: formatTransactionDate(paidAt || new Date()),
        amount: invoice.amount || 0,
        status: 'Paid',
        initials: clientName.substring(0, 2).toUpperCase()
      }, { onConflict: 'id' });
      if (trxError) {
        setSubscriptionInvoices(previousInvoices);
        console.error('Erro ao lançar receita:', trxError);
        alert(`Erro ao lançar receita: ${trxError.message}`);
      }
    } else {
      const { error: trxDeleteError } = await supabase.from('transactions').delete().eq('id', transactionId);
      if (trxDeleteError) {
        setSubscriptionInvoices(previousInvoices);
        console.error('Erro ao remover receita:', trxDeleteError);
        alert(`Erro ao remover receita: ${trxDeleteError.message}`);
      }
    }
    if (invoiceError) {
      setSubscriptionInvoices(previousInvoices);
      console.error('Erro ao atualizar fatura:', invoiceError);
      alert(`Erro ao atualizar fatura: ${invoiceError.message}`);
    }
    if (!invoiceError) await syncAllData();
  };

  return (
    <div className="p-4 md:p-8 pb-32 md:pb-12 max-w-7xl mx-auto flex flex-col gap-6 md:gap-10">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-black text-text-main tracking-tight">Faturas</h2>
          <p className="text-text-secondary font-bold text-base md:text-lg">Controle de cobranças recorrentes.</p>
        </div>
      </header>

      <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide pb-2 md:pb-0">
        <div className="flex gap-2 p-1.5 bg-gray-100/60 rounded-[23px] w-fit min-w-full md:min-w-0">
          {[
            { id: 'open', label: `Em aberto (${openCount})` },
            { id: 'overdue', label: `Em atraso (${overdueCount})` },
            { id: 'paid', label: `Pagas (${paidCount})` }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as InvoiceTab)} className={`px-6 md:px-8 py-3.5 rounded-[22px] text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-1 md:flex-none ${activeTab === tab.id ? 'bg-white shadow-md text-primary' : 'text-text-secondary hover:text-text-main'}`}>{tab.label}</button>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 md:p-8 rounded-[23px] border border-gray-100 shadow-sm flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black uppercase text-text-secondary">Buscar cliente</label>
            <input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" placeholder="Nome do cliente" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-black uppercase text-text-secondary">Período</label>
            <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="h-12 px-4 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
              <option value="all">Todos</option>
              {periodOptions.map(period => (
                <option key={period} value={period}>{period}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setFilterSearch('');
                setFilterPeriod('all');
              }}
              className="h-12 px-6 rounded-2xl bg-gray-100 text-text-secondary font-black uppercase tracking-widest text-[10px]"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[23px] border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-100 text-[9px] font-black uppercase tracking-widest text-text-secondary">
          <span className="col-span-3">Cliente</span>
          <span className="col-span-2">Plano</span>
          <span className="col-span-2">Valor</span>
          <span className="col-span-2">Vencimento</span>
          <span className="col-span-2">Status</span>
          <span className="col-span-1">Ação</span>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredInvoices.map(inv => {
            const client = clients.find(c => c.id === inv.clientId);
            const subscription = subscriptions.find(s => s.id === inv.subscriptionId);
            const planName = subscription?.planId ? subscriptionPlans.find(p => p.id === subscription.planId)?.name || 'Plano removido' : 'Personalizado';
            const status = getStatus(inv);
            const statusLabel = status === 'paid' ? 'Pago' : status === 'overdue' ? 'Em atraso' : 'Em aberto';
            const statusClass = status === 'paid' ? 'bg-green-50 text-green-600' : status === 'overdue' ? 'bg-red-50 text-red-600' : 'bg-primary/10 text-primary';

            return (
              <div key={inv.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 md:items-center">
                <div className="md:col-span-3 min-w-0">
                  <p className="text-sm font-black text-text-main truncate">{client?.name || 'Cliente removido'}</p>
                  <p className="text-[9px] font-black uppercase tracking-widest text-text-secondary mt-1">{inv.periodRef}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-black text-text-main">{planName}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-black text-primary">{formatCurrency(inv.amount || 0)}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm font-black text-text-main">{formatDateBr(inv.dueDate)}</p>
                </div>
                <div className="md:col-span-2">
                  <span className={`w-fit px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${statusClass}`}>{statusLabel}</span>
                </div>
                <div className="md:col-span-1">
                  {status !== 'paid' ? (
                    <button
                      type="button"
                      onClick={() => { void setInvoicePaid(inv, true); }}
                      className="h-10 px-4 rounded-2xl bg-primary/10 text-primary font-black uppercase tracking-widest text-[9px]"
                    >
                      Pagar
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { void setInvoicePaid(inv, false); }}
                      className="h-10 px-4 rounded-2xl bg-gray-100 text-text-secondary font-black uppercase tracking-widest text-[9px]"
                    >
                      Reabrir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filteredInvoices.length === 0 && (
            <div className="p-10 text-center text-text-secondary text-sm font-bold">Nenhuma fatura encontrada.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const MainLayout = ({ children }: { children?: React.ReactNode }) => (
  <div className="flex h-screen w-full bg-background-light text-text-main font-display overflow-hidden relative">
    <Sidebar />
    <div className="flex-1 flex flex-col h-full overflow-hidden w-full">
      <Header />
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        {children}
      </main>
      <FirstLoginOverlay />
    </div>
  </div>
);

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [financialCategories, setFinancialCategories] = useState<FinancialCategory[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionInvoices, setSubscriptionInvoices] = useState<SubscriptionInvoice[]>([]);
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [roles, setRoles] = useState<Role[]>(INITIAL_ROLES);
  const [company, setCompany] = useState<CompanyInfo>(INITIAL_COMPANY);
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isGeneratingInvoices = useRef(false);

  const syncAllData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const results = await Promise.allSettled([
        supabase.from('clients').select('*'),
        supabase.from('projects').select('*'),
        supabase.from('tasks').select('*'),
        supabase.from('transactions').select('*'),
        supabase.from('suppliers').select('*'),
        supabase.from('expenses').select('*'),
        supabase.from('financial_categories').select('*'),
        supabase.from('subscription_plans').select('*'),
        supabase.from('subscriptions').select('*'),
        supabase.from('subscription_invoices').select('*'),
        supabase.from('company_info').select('*'),
        supabase.from('profiles').select('*')
      ]);

      const data = results.map(r => r.status === 'fulfilled' ? r.value.data : null);

      if (data[0]) setClients((data[0] as any[]).map(mapClient));
      if (data[1]) setProjects((data[1] as any[]).map(mapProject));
      if (data[2]) setTasks((data[2] as any[]).map(mapTask));
      if (data[3]) setTransactions((data[3] as any[]).map(mapTransaction));
      if (data[4]) setSuppliers((data[4] as any[]).map(mapSupplier));
      if (data[5]) setExpenses((data[5] as any[]).map(mapExpense));
      if (data[6]) setFinancialCategories((data[6] as any[]).map(mapFinancialCategory));
      if (data[7]) setSubscriptionPlans((data[7] as any[]).map(mapSubscriptionPlan));
      if (data[8]) setSubscriptions((data[8] as any[]).map(mapSubscription));
      if (data[9]) setSubscriptionInvoices((data[9] as any[]).map(mapSubscriptionInvoice));
      
      const companyData = data[10] as any[];
      if (companyData && companyData.length > 0) {
        const raw = companyData[0];
        console.log('🔍 Company data carregada do banco:', raw);
        setCompany({
          id: raw.id,
          name: raw.name || '',
          cnpj: raw.cnpj || '',
          address: raw.address || '',
          phone: raw.phone || '',
          email: raw.email || '',
          logoUrl: raw.logo_url || '',
          briefingTemplate: raw.briefing_template || []
        });
      }
      
      const profilesRaw = (data[11] as any[]) || [];
      const mappedProfiles = profilesRaw.map(p => ({
        id: p.id,
        name: p.name || 'Membro',
        email: p.email || 'N/A', 
        role: p.role,
        avatarUrl: p.avatar_url || USER_PROFILE_IMAGE,
        isFirstLogin: p.is_first_login
      }));
      setProfiles(mappedProfiles);
      
      if (session?.user) {
        const myProfile = profilesRaw.find(p => p.id === session.user.id);
        if (myProfile) {
          setCurrentUser({
            id: myProfile.id,
            name: myProfile.name || 'Usuário',
            email: myProfile.email || session.user.email!,
            role: myProfile.role as any,
            avatarUrl: myProfile.avatar_url || USER_PROFILE_IMAGE,
            isFirstLogin: myProfile.is_first_login
          });
        }
      }
    } catch (err) {
      console.error('Erro na sincronização:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setCurrentUser(null);
      await supabase.auth.signOut();
      window.location.reload();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
       await syncAllData();
       supabase.auth.onAuthStateChange(async (event) => {
         if (event === 'SIGNED_IN') await syncAllData();
         else if (event === 'SIGNED_OUT') setCurrentUser(null);
       });
    };
    initAuth();
    
    const channel = supabase.channel('public:all').on('postgres_changes', { event: '*', schema: 'public' }, syncAllData).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (!subscriptions.length || isGeneratingInvoices.current) return;
    const run = async () => {
      isGeneratingInvoices.current = true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentPeriod = getPeriodRef(today);
      const startOfPeriod = new Date(today.getFullYear(), today.getMonth(), 1);
      const inserts: any[] = [];

      subscriptions.forEach((sub, index) => {
        if (!sub.enabled) return;
        const cancelDate = parseDateOnly(sub.cancelDate);
        if (cancelDate && cancelDate < startOfPeriod) return;
        const startDate = parseDateOnly(sub.startDate);
        const currentDueDate = getDueDateForPeriod(currentPeriod, sub.dueDay || 5);
        const targetPeriod = startDate && startDate > currentDueDate ? getNextPeriodRef(currentPeriod) : currentPeriod;
        const dueDate = getDueDateForPeriod(targetPeriod, sub.dueDay || 5);
        const existing = subscriptionInvoices.find(inv => inv.subscriptionId === sub.id && inv.periodRef === targetPeriod);
        if (existing) return;
        const triggerDate = new Date(dueDate);
        triggerDate.setDate(dueDate.getDate() - 15);
        if (today < triggerDate) return;
        inserts.push({
          id: `inv-${sub.id}-${targetPeriod}-${index}`,
          subscription_id: sub.id,
          client_id: sub.clientId,
          period_ref: targetPeriod,
          issue_date: formatDateOnly(today),
          due_date: formatDateOnly(dueDate),
          amount: sub.monthlyValue || 0,
          status: 'Open'
        });
      });

      if (inserts.length) {
        await supabase.from('subscription_invoices').insert(inserts);
        await syncAllData();
      }
      isGeneratingInvoices.current = false;
    };
    void run();
  }, [subscriptions, subscriptionInvoices, syncAllData]);

  if (isLoading) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-background-light font-display">
        <div className="bg-primary size-12 rounded-2xl animate-spin shadow-xl shadow-primary/20 flex items-center justify-center">
          <span className="material-symbols-outlined text-white text-2xl">sync</span>
        </div>
        <p className="mt-6 text-[9px] font-black uppercase tracking-[0.4em] text-text-secondary animate-pulse">Carregando Magenta...</p>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ 
      clients, projects, tasks, transactions, suppliers, expenses, financialCategories, subscriptionPlans, subscriptions, subscriptionInvoices, profiles, roles, company,
      setClients, setProjects, setTasks, setTransactions, setSuppliers, setExpenses, setFinancialCategories, setSubscriptionPlans, setSubscriptions, setSubscriptionInvoices, setProfiles, setRoles, setCompany,
      searchTerm, setSearchTerm, currentUser, setCurrentUser, sidebarOpen, setSidebarOpen, syncAllData, handleLogout
    }}>
      <Router>
        <Routes>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/*" element={
            currentUser ? (
              <MainLayout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/briefings" element={<Briefings />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/kanban" element={<Kanban />} />
                  <Route path="/financials" element={<Financials />} />
                  <Route path="/subscriptions" element={<Subscriptions />} />
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/profile" element={<Profile />} />
                </Routes>
              </MainLayout>
            ) : (
              <ErpLogin onLogin={setCurrentUser} company={company} />
            )
          } />
        </Routes>
      </Router>
    </AppContext.Provider>
  );
};

export default App;
